/* eslint-disable @typescript-eslint/no-explicit-any */
import React from "react";
import NumberField from "../inputs/NumberField";
import Form from "../../ui/Form/Form";
import CopyButton from "../buttons/CopyButton";
import HexInput from "../../ui/HexInput";
import DropdownField from "../inputs/DropdownField";
import DropdownSearchField from "../inputs/DropdownSearchField";
import {
  FORM_VALIDATION_TRANSLATIONS,
  SIDEPANEL_GENERAL_TRANSLATIONS,
} from "../../translations/util";
import CheckBox from "../checkbox/CheckBox";

interface IProps {
  esRef: string;
  data: any;
  setData: any;
  isEditing: boolean;
  formFields: any;
  dfsiVersion: number | undefined;
  isChecked: boolean;
  setIsChecked: any;
}

const EditDFSIChannelRowFormFields = ({
  esRef,
  data,
  setData,
  isEditing,
  formFields,
  dfsiVersion,
  isChecked,
  setIsChecked,
}: IProps) => {
  return (
    <Form>
      <div className="readonly-data">
        <ul>
          <li className="flex items-center" data-cy="referenceID">
            <h5>
              {SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields["Reference ID"]}
            </h5>
            <div className="data-content">
              <span
                className={(data.Ref || esRef).length > 20 ? "truncate" : ""}
              >
                {data.Ref || esRef}
              </span>
              <CopyButton copyContent={data.Ref || esRef} />
            </div>
          </li>

          <li className="flex items-center" data-cy="creationDate">
            {data.CreationDate ? (
              <div className="flex items-center mr-3">
                <h5>
                  {
                    SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields
                      .CreationDate
                  }
                </h5>
                <div className="data-content">
                  <span> {data.CreationDate} </span>
                </div>
              </div>
            ) : null}

            {data.ModificationDate && (
              <div className="flex items-center" data-cy="modificationDate">
                <h5>
                  {
                    SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields
                      .ModificationDate
                  }
                </h5>
                <div className="data-content">
                  <span> {data.ModificationDate} </span>
                </div>
              </div>
            )}
          </li>

          {data["CONNECTION-ID"] ? (
            <li className="flex items-center " data-cy="connectionID">
              <h5>
                {
                  SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields[
                    "CONNECTION-ID"
                  ]
                }
              </h5>
              <div className="data-content">
                <span> {data["CONNECTION-ID"]} </span>
                <CopyButton copyContent={data["CONNECTION-ID"]} />
              </div>
            </li>
          ) : null}

          {data["CHANNEL-ID"] ? (
            <li className="flex items-center " data-cy="channelID">
              <h5>
                {SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields["Channel ID"]}
              </h5>
              <div className="data-content">
                <span> {data["CHANNEL-ID"]} </span>
                <CopyButton copyContent={data["CHANNEL-ID"]} />
              </div>
            </li>
          ) : null}

          {dfsiVersion ? (
            <li className="flex items-center " data-cy="dfsiVersion">
              <h5>
                {
                  SIDEPANEL_GENERAL_TRANSLATIONS.nonEditableFields[
                    "DFSI-VERSION"
                  ]
                }
              </h5>
              <div className="data-content">
                <span> {dfsiVersion} </span>
              </div>
            </li>
          ) : null}
        </ul>
      </div>

      {formFields.map(
        (rows: {
          cols: number;
          fields: {
            name: string;
            tooltipText?: string;
            label: string;
            type: string;
            invalidText?: string | null;
            isInputInvalid?: (
              chanelMode?: string,
              val?: string | number
            ) => boolean;
            comboItems: ({
              fieldName,
              dfsiVersion,
              channelMode,
              ctcValue,
              cdcValue,
            }: {
              fieldName?: string;
              dfsiVersion?: number;
              channelMode?: string;
              ctcValue?: string;
              cdcValue?: string;
            }) => string[];
            min?: number;
            max?: number;
            items?: string[];
            disabled?: (
              chanelMode?: string,
              dfsiVersion?: number,
              isChecked?: boolean
            ) => boolean;
          }[];
        }) => {
          return (
            <div
              className={`grid grid-cols-${rows.cols} gap-4 form-control single-item`}
            >
              {rows.fields.map((field) => {
                switch (field.type) {
                  case "CHECKBOX":
                    return (
                      <div className="dfsi-channel-checkbox">
                        <CheckBox
                          labelText={field.label}
                          id={field.label}
                          defaultChecked={isChecked}
                          disabled={
                            !isEditing ||
                            field.disabled?.(data["CHANNEL-MODE"], dfsiVersion)
                          }
                          invalid={field.isInputInvalid?.(
                            data["CHANNEL-MODE"],
                            dfsiVersion
                          )}
                          invalidText={
                            field.invalidText ||
                            `"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`
                          }
                          onChange={(e) => {
                            setIsChecked(!isChecked);
                            setData({
                              ...data,
                              [field.name]: e.target.checked,
                              "CTC-SQUELCH-SYSTEM": "None",
                              "CDC-SQUELCH-SYSTEM": "None",
                            });
                          }}
                          checked={false}
                        />
                      </div>
                    );
                  case "NUMBER":
                    return (
                      <NumberField
                        onChange={(val) => {
                          if (!val || !Number.isNaN(+val))
                            setData({
                              ...data,
                              [field.name]: val ? +val : undefined,
                            });
                        }}
                        label={field.label}
                        placeholder={field.label}
                        max={field.max || 0}
                        min={field.min || 0}
                        disabled={
                          !isEditing || field.disabled?.(data["CHANNEL-MODE"])
                        }
                        invalid={
                          !isEditing
                            ? false
                            : field.isInputInvalid?.(
                                data["CHANNEL-MODE"],
                                data[field.name] as number
                              )
                        }
                        value={data[field.name] as number}
                        invalidText={
                          field.invalidText ||
                          `"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`
                        }
                        tooltipText={field.tooltipText}
                        required
                      />
                    );
                  case "HEX":
                    return (
                      <HexInput
                        onChange={(val) => {
                          setData({
                            ...data,
                            [field.name]: val || val === 0 ? +val : undefined,
                          });
                        }}
                        label={field.label}
                        placeholder={field.label}
                        max={field.max || 0}
                        min={field.min || 0}
                        disabled={
                          !isEditing || field.disabled?.(data["CHANNEL-MODE"])
                        }
                        invalid={
                          !isEditing
                            ? false
                            : field.isInputInvalid?.(
                                data["CHANNEL-MODE"],
                                data[field.name] as number
                              )
                        }
                        value={data[field.name] as number}
                        invalidText={
                          field.invalidText ||
                          `"${field.label}" ${FORM_VALIDATION_TRANSLATIONS.range} (${field.min}-${field.max})`
                        }
                        tooltipText={field.tooltipText}
                        required
                      />
                    );
                  case "COMBO-DROPDOWN":
                    return (
                      <DropdownSearchField
                        className="dropdown-field"
                        label={field.label}
                        placeholder={field.label}
                        items={
                          field.comboItems({
                            fieldName: field.name,
                            dfsiVersion,
                            channelMode: data["CHANNEL-MODE"],
                            ctcValue: data["CTC-SQUELCH-SYSTEM"],
                            cdcValue: data["CDC-SQUELCH-SYSTEM"],
                          }) ?? []
                        }
                        selectedItem={
                          data[field?.name] && data[field.name].toString()
                        }
                        onChange={({
                          selectedItem,
                        }: {
                          selectedItem: string;
                        }) => {
                          setData({
                            ...data,
                            [field.name]: selectedItem,
                          });
                        }}
                        tooltipText={field.tooltipText}
                        disabled={
                          !isEditing ||
                          field.disabled?.(
                            data["CHANNEL-MODE"],
                            data["DFSI-VERSION"],
                            isChecked
                          )
                        }
                        required
                      />
                    );
                  default:
                    return (
                      <DropdownField
                        className="dropdown-field"
                        label={field.label}
                        selectedItem={data[field.name]}
                        items={field.items || []}
                        onChange={({
                          selectedItem,
                        }: {
                          selectedItem: string;
                        }) =>
                          setData({
                            ...data,
                            [field.name]: selectedItem,
                          })
                        }
                        tooltipText={field.tooltipText}
                        required
                        disabled={!isEditing}
                      />
                    );
                }
              })}
            </div>
          );
        }
      )}
    </Form>
  );
};

export default EditDFSIChannelRowFormFields;
